import { wrappingInputRule } from 'prosemirror-inputrules';
import type { ParseSpec } from 'prosemirror-markdown';
import type { NodeSpec, Node as ProsemirrorNode } from 'prosemirror-model';
import type { EditorState } from 'prosemirror-state';
import { findParentNode } from 'prosemirror-utils';
import toggleList from '../commands/toggleList';
import type { MarkdownSerializerState } from '../lib/markdown/serializer';
import type { Dispatch } from '../types';
import type { NodeOptions } from './Node';
import Node from './Node';

export default class OrderedList extends Node {
	get name() {
		return 'ordered_list';
	}

	get schema(): NodeSpec {
		return {
			attrs: {
				order: {
					default: 1,
				},
			},
			content: 'list_item+',
			group: 'block',
			parseDOM: [
				{
					tag: 'ol',
					getAttrs: (dom: HTMLElement | string) =>
						typeof dom === 'string'
							? null
							: {
									order: dom.hasAttribute('start')
										? parseInt(dom.getAttribute('start') || '1', 10)
										: 1,
								},
				},
			],
			toDOM: (node) =>
				node.attrs.order === 1
					? ['ol', 0]
					: ['ol', { start: node.attrs.order }, 0],
		};
	}

	commands({ type, schema }: NodeOptions) {
		return () => toggleList(type, schema.nodes.list_item);
	}

	keys({ type, schema }: NodeOptions) {
		return {
			'Shift-Ctrl-9': toggleList(type, schema.nodes.list_item),
			Backspace: (state: EditorState, dispatch?: Dispatch) => {
				// Handle backspace at the start of a top-level ordered list item.
				// Converts the list item to a paragraph using toggleList.
				const { $from, empty } = state.selection;
				// Only handle cursor selections at the start of paragraph
				if (!empty || $from.parentOffset !== 0) return false;
				// Don't handle empty paragraphs - let ListItem's joinOrRemoveEmptyListItem handle them
				if ($from.parent.content.size === 0) return false;
				// Verify we're inside an ordered_list
				const parentList = findParentNode(
					(node) => node.type === schema.nodes.ordered_list
				)(state.selection);
				if (!parentList) return false;
				// Check if this is a top-level list (not nested inside another list item)
				// by walking up from the current selection to find if there's a list_item
				// ancestor that contains the ordered_list
				for (let d = $from.depth; d > parentList.depth; d -= 1) {
					const node = $from.node(d);
					if (['list_item', 'checkbox_item'].includes(node.type.name)) {
						// Nested list - don't handle
						return false;
					}
				}
				// Convert list item to paragraph
				return toggleList(type, schema.nodes.list_item)(state, dispatch);
			},
		};
	}

	inputRules({ type }: NodeOptions) {
		return [
			wrappingInputRule(
				/^(\d+)\.\s$/,
				type,
				(match) => ({ order: +match[1] }),
				(match, node) => node.childCount + node.attrs.order === +match[1]
			),
		];
	}

	toMarkdown(state: MarkdownSerializerState, node: ProsemirrorNode) {
		state.write('\n');

		const start = node.attrs.order !== undefined ? node.attrs.order : 1;
		const maxW = `${start + node.childCount - 1}`.length;
		const space = state.repeat(' ', maxW + 2);

		state.renderList(node, space, (i: number) => {
			const nStr = `${start + i}`;
			return `${state.repeat(' ', maxW - nStr.length) + nStr}. `;
		});
	}

	parseMarkdown(): ParseSpec {
		return {
			block: 'ordered_list',
			getAttrs: (tok) => ({
				order: parseInt(tok.attrGet('start') || '1', 10),
			}),
		};
	}
}
